/*
 * UAV Application Model — Shopify section component.
 * Requires Three.js r128 + GLTFLoader r128 (loaded lazily from theme assets).
 * Safe for multiple instances and Shopify Theme Editor load/unload.
 */
(function () {
  'use strict';

  if (customElements.get('sunstone-uav-model')) return;

  var TARGET_SPAN = 6.6; // normalized world size of the aircraft
  var NOSE_POSITIVE = true; // flip if hotspots land mirrored front-to-back
  var INITIAL_TILT = { x: 0.12, y: -0.65 };
  var CAMERA_Z_DEFAULT = 9.4;

  var libPromises = {};

  function loadScript(src) {
    if (!libPromises[src]) {
      libPromises[src] = new Promise(function (resolve, reject) {
        var el = document.createElement('script');
        el.src = src;
        el.async = true;
        el.onload = function () { resolve(); };
        el.onerror = function () {
          delete libPromises[src]; // allow retry on next section load
          reject(new Error('Script failed: ' + src));
        };
        document.head.appendChild(el);
      });
    }
    return libPromises[src];
  }

  function debugLog() {
    if (window.SunstoneUavDebug) {
      console.info.apply(console, ['[sunstone-uav]'].concat([].slice.call(arguments)));
    }
  }

  class SunstoneUavModel extends HTMLElement {
      connectedCallback() {
        // Guard: theme editor can re-append the same node
        if (this._connected) return;
        this._connected = true;

        this.abort = new AbortController();
        this.sectionId = this.dataset.sectionId || '';
        this.designMode = this.dataset.designMode === 'true';
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoRotate =
          this.dataset.autoRotate === 'true' && !this.designMode && !this.reducedMotion.matches;
        this.rotateSpeed = (parseFloat(this.dataset.rotateSpeed) || 3) * 0.00073;
        this.enableZoom = this.dataset.enableZoom === 'true';
        this.zoomClose = parseFloat(this.dataset.zoomClose) || 5.2;
        this.zoomFar = parseFloat(this.dataset.zoomFar) || 13;
        this.modelUrl = this.dataset.modelUrl || '';

        this.stage = this.querySelector('[data-uav-stage]');
        this.leadSvg = this.querySelector('[data-uav-lead]');
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-uav-tab]'));
        this.apps = Array.prototype.slice.call(this.querySelectorAll('[data-uav-app]'));
        this.emptyMsg = this.querySelector('[data-uav-empty]');
        this.liveRegion = this.querySelector('[data-uav-live]');
        this.loadingMsg = this.querySelector('[data-uav-loading]');
        this.instructions = this.querySelector('[data-uav-instructions]');
        this.stageControls = this.querySelector('[data-uav-stage-controls]');

        var configEl = this.querySelector('[data-uav-config]');
        this.hotspotConfig = [];
        try {
          this.hotspotConfig = configEl ? JSON.parse(configEl.textContent).hotspots || [] : [];
        } catch (e) {
          debugLog('Bad hotspot config JSON', e);
        }

        this.activeIndex = -1;
        this.ready = false;
        this.failed = false;
        this.initStarted = false;
        this.inView = false;
        this.rafId = 0;
        this.renderRafId = 0;
        this.lastFrameTime = 0;
        this.renderQueued = false;
        this.engaged = false; // becomes true after first pointer interaction on the canvas
        this.sprites = [];

        // Enhance: switch panel from static stack to tab/detail mode
        this.setAttribute('data-enhanced', '');
        this.apps.forEach(function (a) { a.hidden = true; });
        if (this.emptyMsg) this.emptyMsg.hidden = false;

        this.bindUi();

        if (this.dataset.openFirst === 'true' && this.tabs.length) {
          this.selectApp(parseInt(this.tabs[0].dataset.uavIndex, 10), 'default');
        }

        // Lazy 3D init when the section approaches the viewport
        var self = this;
        if (this.modelUrl && this.stage && 'IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                self.inView = entry.isIntersecting;
                if (entry.isIntersecting && !self.initStarted) self.initThree();
                self.updateLoop();
              });
            },
            { rootMargin: '300px 0px' }
          );
          this.io.observe(this.stage);
        } else if (this.modelUrl && this.stage) {
          this.inView = true;
          this.initThree();
        } else {
          this.setAttribute('data-model-state', 'unavailable');
        }

        document.addEventListener('visibilitychange', this.onVisibility.bind(this), {
          signal: this.abort.signal
        });
        var rmHandler = this.onReducedMotion.bind(this);
        if (this.reducedMotion.addEventListener) {
          this.reducedMotion.addEventListener('change', rmHandler, { signal: this.abort.signal });
        }
        document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this), {
          signal: this.abort.signal
        });
      }

      disconnectedCallback() {
        this._connected = false;
        if (this.abort) this.abort.abort();
        if (this.io) this.io.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.stopLoop();
        this.disposeThree();
      }

      /* ---------- Accessible controls (work with or without 3D) ---------- */

      bindUi() {
        var self = this;
        var signal = this.abort.signal;

        this.tabs.forEach(function (btn) {
          btn.addEventListener(
            'click',
            function () {
              var i = parseInt(btn.dataset.uavIndex, 10);
              self.activeIndex === i ? self.closeApp() : self.selectApp(i, 'html-control');
            },
            { signal: signal }
          );
        });

        var cta = this.querySelector('[data-uav-cta]');
        if (cta) {
          cta.addEventListener(
            'click',
            function () {
              self.emit('cta-click', { ctaLabel: cta.textContent.trim(), ctaHref: cta.getAttribute('href') });
            },
            { signal: signal }
          );
        }

        var zoomIn = this.querySelector('[data-uav-zoom-in]');
        var zoomOut = this.querySelector('[data-uav-zoom-out]');
        var reset = this.querySelector('[data-uav-reset]');
        if (zoomIn) zoomIn.addEventListener('click', function () { self.zoomBy(-0.9); }, { signal: signal });
        if (zoomOut) zoomOut.addEventListener('click', function () { self.zoomBy(0.9); }, { signal: signal });
        if (reset) {
          reset.addEventListener(
            'click',
            function () {
              self.resetView();
              self.emit('reset-view', {});
            },
            { signal: signal }
          );
        }
      }

      selectApp(index, source) {
        var self = this;
        this.activeIndex = index;
        if (source !== 'default') {
          this.autoRotate = false;
          this.updateLoop();
        }

        var activeApp = null;
        this.apps.forEach(function (a) {
          var match = parseInt(a.dataset.uavIndex, 10) === index;
          a.hidden = !match;
          if (match) activeApp = a;
        });
        this.tabs.forEach(function (t) {
          t.setAttribute('aria-expanded', String(parseInt(t.dataset.uavIndex, 10) === index));
        });
        if (this.emptyMsg) this.emptyMsg.hidden = true;

        var cfg = this.configFor(index);
        if (this.liveRegion && cfg) this.liveRegion.textContent = 'Showing: ' + cfg.category;
        if (activeApp && activeApp.parentElement) activeApp.parentElement.scrollTop = 0;

        this.emit('app-select', {
          appNumber: cfg ? cfg.number : null,
          appCategory: cfg ? cfg.category : null,
          source: source || 'unknown'
        });
        this.requestRender();
      }

      closeApp() {
        this.activeIndex = -1;
        this.apps.forEach(function (a) { a.hidden = true; });
        this.tabs.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
        if (this.emptyMsg) this.emptyMsg.hidden = false;
        if (this.liveRegion) this.liveRegion.textContent = '';
        this.clearLead();
        this.requestRender();
      }

      configFor(index) {
        for (var i = 0; i < this.hotspotConfig.length; i++) {
          if (this.hotspotConfig[i].index === index) return this.hotspotConfig[i];
        }
        return null;
      }

      emit(name, detail) {
        detail = detail || {};
        detail.sectionId = this.sectionId;
        this.dispatchEvent(new CustomEvent('sunstone-uav:' + name, { bubbles: true, detail: detail }));
      }

      onBlockSelect(e) {
        var blockId = e.detail && e.detail.blockId;
        if (!blockId) return;
        var app = this.apps.filter(function (item) { return item.dataset.blockId === blockId; })[0];
        if (app) this.selectApp(parseInt(app.dataset.uavIndex, 10), 'theme-editor');
      }

      onReducedMotion() {
        if (this.reducedMotion.matches) {
          this.autoRotate = false;
          this.updateLoop();
        }
      }

      onVisibility() {
        this.updateLoop();
      }

      /* ---------- Three.js ---------- */

      initThree() {
        var self = this;
        this.initStarted = true;
        if (this.loadingMsg) this.loadingMsg.hidden = false;
        this.setAttribute('data-model-state', 'loading');

        var hasWebgl = (function () {
          try {
            var c = document.createElement('canvas');
            return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
          } catch (e) {
            return false;
          }
        })();

        if (!hasWebgl) {
          this.modelFailed('WebGL unavailable');
          return;
        }

        loadScript(this.dataset.libThree)
          .then(function () { return loadScript(self.dataset.libLoader); })
          .then(function () {
            if (!self._connected) throw new DOMException('Section disconnected', 'AbortError');
            return fetch(self.modelUrl, { signal: self.abort.signal });
          })
          .then(function (res) {
            if (!res.ok) throw new Error('Model HTTP ' + res.status);
            return res.arrayBuffer();
          })
          .then(function (buffer) { return self.buildScene(buffer); })
          .catch(function (err) {
            if (!self._connected || (err && err.name === 'AbortError')) return;
            self.modelFailed(err);
          });
      }

      buildScene(buffer) {
        var self = this;
        var T = window.THREE;

        this.scene = new T.Scene();
        this.scene.add(new T.HemisphereLight(0xbfe8ff, 0x0a1830, 0.95));
        var key = new T.DirectionalLight(0xffffff, 1.0);
        key.position.set(5, 8, 6);
        this.scene.add(key);
        var rim = new T.DirectionalLight(0x4fd8ff, 0.7);
        rim.position.set(-6, 3, -6);
        this.scene.add(rim);
        var grid = new T.GridHelper(30, 30, 0x16375f, 0x0d2038);
        grid.position.y = -2.5;
        this.scene.add(grid);

        this.camera = new T.PerspectiveCamera(42, 1, 0.1, 100);
        this.camera.position.set(0, 1.4, Math.min(Math.max(CAMERA_Z_DEFAULT, this.zoomClose), this.zoomFar));
        this.camera.lookAt(0, 0, 0);
        this.initialZ = this.camera.position.z;

        try {
          this.renderer = new T.WebGLRenderer({ antialias: true, alpha: true });
        } catch (e) {
          this.modelFailed(e);
          return;
        }
        this.renderer.outputEncoding = T.sRGBEncoding;
        this.renderer.toneMapping = T.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.15;
        this.stage.appendChild(this.renderer.domElement);
        this.renderer.domElement.setAttribute('aria-hidden', 'true');

        this.rig = new T.Group();
        this.scene.add(this.rig);
        this.qT = new T.Quaternion();
        this.AX = new T.Vector3(1, 0, 0);
        this.AY = new T.Vector3(0, 1, 0);
        this.raycaster = new T.Raycaster();
        this.pointerVec = new T.Vector2();
        this.worldVec = new T.Vector3();

        this.sizeRenderer();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.sizeRenderer.bind(this));
          this.resizeObserver.observe(this.stage);
        } else {
          window.addEventListener('resize', this.sizeRenderer.bind(this), { signal: this.abort.signal });
        }

        return new Promise(function (resolve, reject) {
          new T.GLTFLoader().parse(buffer, '', resolve, reject);
        }).then(function (gltf) {
          if (!self._connected) return; // unloaded while parsing
          var root = gltf.scene;
          self.rig.add(root);
          self.rig.quaternion.set(0, 0, 0, 1);
          self.rig.updateMatrixWorld(true);

          var box = self.fitModel(root);
          var meshes = [];
          root.traverse(function (o) { if (o.isMesh) meshes.push(o); });
          root.updateMatrixWorld(true);
          self.addHotspots(box, meshes);

          self.rig.quaternion.setFromEuler(new T.Euler(INITIAL_TILT.x, INITIAL_TILT.y, 0));
          self.initialQuaternion = self.rig.quaternion.clone();

          self.bindCanvas();
          self.ready = true;
          if (self.loadingMsg) self.loadingMsg.hidden = true;
          if (self.instructions) self.instructions.hidden = false;
          if (self.stageControls) self.stageControls.hidden = false;
          self.setAttribute('data-model-state', 'ready');
          self.emit('model-init', {});
          self.requestRender();
          self.updateLoop();
        });
      }

      fitModel(root) {
        var T = window.THREE;
        var b1 = new T.Box3().setFromObject(root);
        var s1 = new T.Vector3();
        b1.getSize(s1);
        root.scale.setScalar(TARGET_SPAN / Math.max(s1.x, s1.y, s1.z));
        var b2 = new T.Box3().setFromObject(root);
        var s2 = new T.Vector3();
        b2.getSize(s2);
        if (s2.x > s2.z) root.rotation.y = Math.PI / 2; // nose-tail axis on X, span on Z
        var b3 = new T.Box3().setFromObject(root);
        var c = new T.Vector3();
        b3.getCenter(c);
        root.position.sub(c);
        return new T.Box3().setFromObject(root);
      }

      makeBadgeTexture(n) {
        var T = window.THREE;
        var c = document.createElement('canvas');
        c.width = c.height = 128;
        var x = c.getContext('2d');
        var accent = getComputedStyle(this).getPropertyValue('--uav-accent').trim() || '#F5871F';
        x.beginPath();
        x.arc(64, 64, 56, 0, 7);
        x.fillStyle = accent;
        x.fill();
        x.lineWidth = 9;
        x.strokeStyle = '#fff';
        x.stroke();
        x.fillStyle = '#fff';
        x.font = '600 60px system-ui';
        x.textAlign = 'center';
        x.textBaseline = 'middle';
        x.fillText(String(n), 64, 68);
        return new T.CanvasTexture(c);
      }

      addHotspots(box, meshes) {
        var self = this;
        var T = window.THREE;
        var min = box.min;
        var sz = new T.Vector3();
        box.getSize(sz);
        var rc = new T.Raycaster();

        this.hotspotConfig.forEach(function (cfg) {
          var lf = NOSE_POSITIVE ? cfg.len : 1 - cfg.len;
          var p = new T.Vector3(
            min.x + lf * sz.x,
            min.y + cfg.up * sz.y,
            min.z + (0.5 + cfg.side / 2) * sz.z
          );
          var top = cfg.up >= 0.5;
          rc.set(
            new T.Vector3(p.x, top ? box.max.y + 2 : box.min.y - 2, p.z),
            new T.Vector3(0, top ? -1 : 1, 0)
          );
          var ix = rc.intersectObjects(meshes, true);
          if (!ix.length) {
            var zs = p.z >= 0 ? 1 : -1; // fallback: sweep in from the side at this station
            rc.set(
              new T.Vector3(p.x, p.y, zs * (Math.max(Math.abs(box.max.z), Math.abs(box.min.z)) + 2)),
              new T.Vector3(0, 0, -zs)
            );
            ix = rc.intersectObjects(meshes, true);
          }
          if (ix.length) p = ix[0].point.clone().sub(rc.ray.direction.clone().multiplyScalar(0.07));

          var sprite = new T.Sprite(
            new T.SpriteMaterial({ map: self.makeBadgeTexture(cfg.number), depthTest: false })
          );
          sprite.position.copy(p);
          sprite.scale.set(0.44, 0.44, 1);
          sprite.userData.index = cfg.index;
          self.rig.add(sprite);
          self.sprites.push(sprite);
        });
      }

      bindCanvas() {
        var self = this;
        var cv = this.renderer.domElement;
        var signal = this.abort.signal;
        var dragging = false;
        var rotating = false;
        var px = 0, py = 0, moved = 0;

        cv.addEventListener(
          'pointerdown',
          function (e) {
            self.engaged = true;
            dragging = true;
            rotating = e.pointerType !== 'touch'; // touch waits for an intentional horizontal drag
            moved = 0;
            px = e.clientX;
            py = e.clientY;
            if (cv.setPointerCapture) cv.setPointerCapture(e.pointerId);
            cv.classList.add('is-dragging');
          },
          { signal: signal }
        );

        var endDrag = function () {
          dragging = false;
          rotating = false;
          cv.classList.remove('is-dragging');
        };
        cv.addEventListener('pointerup', endDrag, { signal: signal });
        cv.addEventListener('pointercancel', endDrag, { signal: signal });

        cv.addEventListener(
          'pointermove',
          function (e) {
            if (!dragging) {
              if (self.ready) cv.style.cursor = self.hitTest(e) != null ? 'pointer' : 'grab';
              return;
            }
            var dx = e.clientX - px;
            var dy = e.clientY - py;
            moved += Math.abs(dx) + Math.abs(dy);
            if (!rotating) {
              if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) rotating = true;
              else return; // vertical touch movement stays with page scroll (touch-action: pan-y)
            }
            self.rig.quaternion.premultiply(self.qT.setFromAxisAngle(self.AY, dx * 0.006));
            self.rig.quaternion.premultiply(self.qT.setFromAxisAngle(self.AX, dy * 0.006));
            px = e.clientX;
            py = e.clientY;
            self.autoRotate = false;
            self.updateLoop();
            self.requestRender();
          },
          { signal: signal }
        );

        cv.addEventListener(
          'wheel',
          function (e) {
            if (!self.enableZoom || !self.engaged) return; // page scroll wins until the visitor engages the stage
            var next = Math.max(self.zoomClose, Math.min(self.zoomFar, self.camera.position.z + e.deltaY * 0.004));
            if (next === self.camera.position.z) return; // at zoom boundary: let the page scroll
            e.preventDefault();
            self.camera.position.z = next;
            self.requestRender();
          },
          { passive: false, signal: signal }
        );

        cv.addEventListener(
          'click',
          function (e) {
            if (moved > 6 || !self.ready) return;
            var hit = self.hitTest(e);
            if (hit != null) {
              self.activeIndex === hit ? self.closeApp() : self.selectApp(hit, 'canvas-hotspot');
            } else {
              self.closeApp();
            }
          },
          { signal: signal }
        );
      }

      hitTest(e) {
        var cv = this.renderer.domElement;
        var r = cv.getBoundingClientRect();
        this.pointerVec.x = ((e.clientX - r.left) / r.width) * 2 - 1;
        this.pointerVec.y = -((e.clientY - r.top) / r.height) * 2 + 1;
        this.raycaster.setFromCamera(this.pointerVec, this.camera);
        var ix = this.raycaster.intersectObjects(this.sprites);
        return ix.length ? ix[0].object.userData.index : null;
      }

      zoomBy(delta) {
        if (!this.camera) return;
        this.camera.position.z = Math.max(this.zoomClose, Math.min(this.zoomFar, this.camera.position.z + delta));
        this.requestRender();
      }

      resetView() {
        if (!this.rig || !this.camera) return;
        if (this.initialQuaternion) this.rig.quaternion.copy(this.initialQuaternion);
        this.camera.position.z = this.initialZ;
        this.requestRender();
      }

      sizeRenderer() {
        if (!this.renderer || !this.stage) return;
        var w = this.stage.clientWidth;
        var h = this.stage.clientHeight;
        if (!w || !h) return;
        this.renderer.setSize(w, h);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this.requestRender();
      }

      /* ---------- Render loop: continuous only while auto-rotating ---------- */

      updateLoop() {
        var shouldRun =
          this.ready && this.autoRotate && this.inView && !document.hidden && !this.reducedMotion.matches;
        if (shouldRun && !this.rafId) {
          var self = this;
          this.lastFrameTime = performance.now();
          var tick = function (now) {
            var elapsed = Math.min(Math.max(now - self.lastFrameTime, 0), 50);
            self.lastFrameTime = now;
            self.rig.quaternion.premultiply(
              self.qT.setFromAxisAngle(self.AY, self.rotateSpeed * elapsed / (1000 / 60))
            );
            self.renderFrame();
            self.rafId = requestAnimationFrame(tick);
          };
          this.rafId = requestAnimationFrame(tick);
        } else if (!shouldRun) {
          this.stopLoop();
        }
      }

      stopLoop() {
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = 0;
        }
        this.lastFrameTime = 0;
      }

      requestRender() {
        if (!this.ready || this.rafId || this.renderQueued) return;
        var self = this;
        this.renderQueued = true;
        this.renderRafId = requestAnimationFrame(function () {
          self.renderRafId = 0;
          self.renderQueued = false;
          self.renderFrame();
        });
      }

      renderFrame() {
        if (!this.renderer) return;
        this.renderer.render(this.scene, this.camera);
        this.updateLead();
      }

      /* ---------- Lead line (desktop only) ---------- */

      updateLead() {
        if (!this.leadSvg) return;
        var sprite = null;
        for (var i = 0; i < this.sprites.length; i++) {
          if (this.sprites[i].userData.index === this.activeIndex) { sprite = this.sprites[i]; break; }
        }
        var activeApp = this.apps.filter(function (a) { return !a.hidden; })[0];
        if (this.activeIndex < 0 || !sprite || !activeApp) {
          this.clearLead();
          return;
        }

        var cv = this.renderer.domElement;
        sprite.getWorldPosition(this.worldVec);
        this.worldVec.project(this.camera);

        var cvr = cv.getBoundingClientRect();
        var layout = this.leadSvg.parentElement.getBoundingClientRect();
        var panel = activeApp.closest('.sunstone-uav__panel');
        var pr = panel ? panel.getBoundingClientRect() : null;

        if (!pr || pr.left < cvr.right - 4) {
          this.clearLead(); // panel is stacked (mobile) — no line
          return;
        }

        var hx = (this.worldVec.x * 0.5 + 0.5) * cvr.width + cvr.left - layout.left;
        var hy = (-this.worldVec.y * 0.5 + 0.5) * cvr.height + cvr.top - layout.top;
        var ex = pr.left - layout.left;
        var ey = Math.max(pr.top - layout.top + 20, Math.min(hy, pr.bottom - layout.top - 20));
        var mx = (hx + ex) / 2;

        if (!this.leadPath) {
          var NS = 'http://www.w3.org/2000/svg';
          this.leadPath = document.createElementNS(NS, 'path');
          this.leadPath.setAttribute('fill', 'none');
          this.leadPath.setAttribute('stroke-width', '1.6');
          this.leadDot = document.createElementNS(NS, 'rect');
          this.leadDot.setAttribute('width', '8');
          this.leadDot.setAttribute('height', '8');
          this.leadSvg.appendChild(this.leadPath);
          this.leadSvg.appendChild(this.leadDot);
        }
        var accent = getComputedStyle(this).getPropertyValue('--uav-accent').trim() || '#F5871F';
        this.leadPath.setAttribute('stroke', accent);
        this.leadDot.setAttribute('fill', accent);
        this.leadPath.setAttribute('d', 'M' + hx + ' ' + hy + ' L' + mx + ' ' + hy + ' L' + mx + ' ' + ey + ' L' + ex + ' ' + ey);
        this.leadDot.setAttribute('x', String(ex - 4));
        this.leadDot.setAttribute('y', String(ey - 4));
        this.leadPath.style.display = '';
        this.leadDot.style.display = '';
      }

      clearLead() {
        if (this.leadPath) {
          this.leadPath.style.display = 'none';
          this.leadDot.style.display = 'none';
        }
      }

      /* ---------- Failure and cleanup ---------- */

      modelFailed(err) {
        debugLog('Model failed:', err);
        this.failed = true;
        this.ready = false;
        if (this.loadingMsg) this.loadingMsg.hidden = true;
        if (this.stageControls) this.stageControls.hidden = true;
        this.setAttribute('data-model-state', 'failed');
        this.disposeThree();
        this.emit('model-error', { reason: err && err.message ? err.message : String(err) });
        // Poster + HTML application controls remain fully functional (Level 2)
      }

      disposeThree() {
        var self = this;
        this.stopLoop();
        if (this.renderRafId) {
          cancelAnimationFrame(this.renderRafId);
          this.renderRafId = 0;
          this.renderQueued = false;
        }
        if (this.scene) {
          this.scene.traverse(function (obj) {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
              var mats = Array.isArray(obj.material) ? obj.material : [obj.material];
              mats.forEach(function (m) {
                if (m.map) m.map.dispose();
                m.dispose();
              });
            }
          });
          this.scene = null;
        }
        this.sprites = [];
        if (this.renderer) {
          this.renderer.dispose();
          if (this.renderer.forceContextLoss) {
            try { this.renderer.forceContextLoss(); } catch (e) { /* context may already be lost */ }
          }
          if (this.renderer.domElement && this.renderer.domElement.parentNode) {
            this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
          }
          this.renderer = null;
        }
      }
  }

  customElements.define('sunstone-uav-model', SunstoneUavModel);
})();
